import { useRef, useCallback, useState, useEffect, lazy, Suspense, type ComponentType } from 'react';
import AppShell from './components/AppShell';
import ArtPicker from './components/ArtPicker';
import PhoneFrame from './components/PhoneFrame';
import GuestProvider from './components/GuestProvider';
import { useAuth } from './hooks/useAuth';
import { useRecordVisit } from './api/metrics';
import { useSubscription } from './hooks/useSubscription';
import { useArts } from './hooks/useArt';
import { Art } from './types';
import { isPhoneDevice } from './frameSize';
import './App.css';

function dismissSplash() {
  const el = document.getElementById('splash');
  if (!el) return;
  el.classList.add('hide');
  setTimeout(() => el.remove(), 300);
}

// Wrap React.lazy so a failed dynamic import (typically caused by a deploy
// happening while the user has the old index.html cached, leaving them
// pointing at hashed asset filenames that no longer exist on the server)
// triggers a one-time hard reload instead of a Suspense crash. The reload
// flag in sessionStorage prevents an infinite loop if the failure is real.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((err) => {
      const RELOAD_KEY = 'gnosis-chunk-reload';
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
        // Return a never-resolving promise so Suspense stays in fallback
        // until the reload happens.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    })
  );
}

const Checkout = lazyWithReload(() => import('./components/Checkout'));
const CheckoutSuccess = lazyWithReload(() => import('./components/CheckoutSuccess'));
const AdminChat = lazyWithReload(() => import('./components/AdminChat'));
// Dev-only. import.meta.env.DEV is a literal at build time, so in a production
// build this is `null`, the route below is dead code, and Rollup drops the
// chunk entirely — /reels does not exist in the shipped bundle.
const ReelsPage = import.meta.env.DEV
  ? lazyWithReload(() => import('./components/ReelsPage'))
  : null;
const Gallery = lazyWithReload(() => import('./components/Gallery'));
const LegalPage = lazyWithReload(() => import('./components/LegalPage'));
// The lander, served at / and /promo. LandingPage is kept as a module — v2
// imports its auth CTAs, phone frame and reveal wrapper — but is no longer
// rendered as a page itself.
const LandingPageV2 = lazyWithReload(() => import('./components/LandingPageV2'));
const AdminMetrics = lazyWithReload(() => import('./components/AdminMetrics'));
const SharedTechnique = lazyWithReload(() => import('./components/SharedTechnique'));

const FRAME_MARGIN = 40;
const FRAME_POS_KEY = 'gnosis-frame-x';

const isPreview = new URLSearchParams(window.location.search).has('preview');

// Capture a referral code from the landing URL (?ref=<referrer uid>) and stash
// it so it survives the sign-in round trip (popup / email link). The referrer
// is credited only after this user actually pays — see Checkout's handleStart
// (records the intent) and the Stripe webhook (grants the month).
(() => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (ref && /^[A-Za-z0-9]{6,128}$/.test(ref)) {
    localStorage.setItem('gnosis-ref', ref);
    // The deal the shared link advertised ('months' | 'cash') — decides what
    // the referrer earns when this user converts. Baked into the link so a
    // referrer's different links can carry different deals.
    const reward = params.get('reward');
    if (reward === 'months' || reward === 'cash') {
      localStorage.setItem('gnosis-ref-reward', reward);
    }
  }
  // Campaign tags, stashed the same way and for the same reason: the sign-in
  // round trip loses the query string, and the dashboard groups people by
  // whichever of these they arrived under. First touch wins (recordVisit
  // never overwrites a source), so a returning visitor keeps their promoter.
  for (const [param, key] of [['utm_source', 'gnosis-utm-source'], ['utm_campaign', 'gnosis-utm-campaign']] as const) {
    const v = params.get(param);
    if (v && /^[\w .@/-]{1,64}$/.test(v)) localStorage.setItem(key, v);
  }
})();

function navigate(to: string, setPath: (p: string) => void) {
  // Preserve query string (needed for ?test=... in test mode, ?preview, etc.)
  const basePath = import.meta.env.BASE_URL.slice(0, -1); // Remove trailing slash
  const fullPath = basePath + to;
  window.history.pushState({}, '', fullPath + window.location.search);
  setPath(to);
}

// Preview-mode wrapper that renders the AppShell inside the phone frame with
// the demo cursor overlay — used by the promo page and the demo-recording
// script so the simulated taps are visible.
function PreviewWithCursor() {
  const [cursor, setCursor] = useState<{ x: number; y: number; visible: boolean; key?: number }>({ x: 0, y: 0, visible: false });
  return (
    <div className="phone-frame-wrapper">
      <PhoneFrame>
        <AppShell preview onDemoCursor={setCursor} />
        {cursor.visible && (
          <div
            key={cursor.key}
            className="demo-cursor"
            style={{ left: cursor.x, top: cursor.y, zIndex: 999999 }}
          />
        )}
      </PhoneFrame>
    </div>
  );
}

function App() {
  const { user, loading, logOut } = useAuth();
  useRecordVisit(user);
  const sub = useSubscription(user);
  const { arts, setArts } = useArts(user?.uid ?? null);
  const subStatus = sub.status;
  const subLoading = sub.loading;
  const [path, setPath] = useState(() => {
    const basePath = import.meta.env.BASE_URL.slice(0, -1); // Remove trailing slash
    return window.location.pathname.startsWith(basePath)
      ? window.location.pathname.slice(basePath.length)
      : window.location.pathname;
  });
  const frameRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; startLeft: number } | null>(null);
  const [frameX, setFrameX] = useState<number | null>(() => {
    const saved = localStorage.getItem(FRAME_POS_KEY);
    return saved ? Number(saved) : null;
  });

  useEffect(() => {
    const onPop = () => {
      const basePath = import.meta.env.BASE_URL.slice(0, -1); // Remove trailing slash
      const currentPath = window.location.pathname.startsWith(basePath)
        ? window.location.pathname.slice(basePath.length)
        : window.location.pathname;
      setPath(currentPath);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Dismiss the HTML splash screen once auth + subscription have resolved
  useEffect(() => {
    if (!loading && !subLoading) dismissSplash();
  }, [loading, subLoading]);

  // Handle post-auth redirect (mobile sign-in redirect flow)
  useEffect(() => {
    if (!loading && !subLoading && user && subStatus !== null) {
      const pendingPath = sessionStorage.getItem('gnosis-post-auth');
      if (pendingPath) {
        sessionStorage.removeItem('gnosis-post-auth');
        const isAdmin = user.uid === 'bZj8y9DApEac3eKOuKV08MKcaDx2';
        const hasAccess = subStatus === 'active' || isAdmin;
        // Skip checkout if user already has access, go straight to app
        if (pendingPath === '/checkout' && hasAccess) {
          navigate('/app', setPath);
        } else {
          navigate(pendingPath, setPath);
        }
      }
    }
  }, [loading, subLoading, user, subStatus]);

  useEffect(() => {
    if (frameX != null) localStorage.setItem(FRAME_POS_KEY, String(frameX));
  }, [frameX]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e: MediaQueryListEvent) => { if (e.matches) setFrameX(null); };
    if (mq.matches) Promise.resolve().then(() => setFrameX(null));
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Clamp frame position on viewport resize (debounced)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const clamp = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const el = frameRef.current;
        if (!el || isPhoneDevice() || window.innerWidth < 641) return;
        setFrameX((prev) => {
          if (prev == null) return prev;
          const wrapperWidth = el.parentElement?.clientWidth ?? window.innerWidth;
          const frameWidth = el.offsetWidth;
          const maxOffset = (wrapperWidth - frameWidth) / 2 - FRAME_MARGIN;
          return Math.max(-maxOffset, Math.min(maxOffset, prev));
        });
      }, 150);
    };
    window.addEventListener('resize', clamp);
    return () => { window.removeEventListener('resize', clamp); clearTimeout(timer); };
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // No panel to drag on a phone, even one claiming a desktop viewport.
    if (isPhoneDevice() || window.innerWidth < 641) return;
    const el = frameRef.current;
    if (!el) return;
    const target = e.target as HTMLElement;
    // Drag the panel by the view header (the "Library"/"Series"/"Discover"
    // bar), not the brand/title.
    if (!target.closest('.view-header') && !target.closest('.fab-brand')) return;
    if (target.closest('button')) return;
    // The Discover intro modal reuses a .view-header as its header row, and it
    // owns its own swipe-up gesture — don't steal the drag from it.
    if (target.closest('.feed-intro-layer')) return;
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startLeft: frameX ?? 0 };
  }, [frameX]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!drag.current) return;
    const el = frameRef.current;
    if (!el) return;
    const wrapperWidth = el.parentElement?.clientWidth ?? window.innerWidth;
    const frameWidth = el.offsetWidth;
    const maxOffset = (wrapperWidth - frameWidth) / 2 - FRAME_MARGIN;
    const delta = e.clientX - drag.current.startX;
    const newX = drag.current.startLeft + delta;
    setFrameX(Math.max(-maxOffset, Math.min(maxOffset, newX)));
  }, []);

  const onPointerUp = useCallback(() => {
    drag.current = null;
  }, []);

  // --- Routing ---

  // Preview mode (for promo page embed / demo recording) — always show app
  if (isPreview) {
    dismissSplash();
    return <PreviewWithCursor />;
  }

  // /gallery — component gallery (no auth required)
  if (path === '/gallery') {
    dismissSplash();
    return <Suspense fallback={null}><Gallery /></Suspense>;
  }

  // Legal pages — no auth required
  if (path === '/terms') { dismissSplash(); return <Suspense fallback={null}><LegalPage page="terms" /></Suspense>; }
  if (path === '/privacy') { dismissSplash(); return <Suspense fallback={null}><LegalPage page="privacy" /></Suspense>; }
  if (path === '/refund') { dismissSplash(); return <Suspense fallback={null}><LegalPage page="refund" /></Suspense>; }
  if (path === '/copyright') { dismissSplash(); return <Suspense fallback={null}><LegalPage page="copyright" /></Suspense>; }

  // /t/<id> — a shared technique link. A visitor who can't get into the app
  // still sees the one clip the link promised; everything else stays walled.
  // Signed-in subscribers fall through to /app below, which opens it in place.
  const sharedId = path.startsWith('/t/') ? path.slice(3).split('/')[0] : null;

  // Still loading — show nothing (but let checkout/success wait for auth to restore)
  if (loading) return null;

  // /reels — the Instagram reel board. Dev-only: in a production build
  // ReelsPage is null and this whole branch is dropped. Sits above the
  // signed-out gate because it reads from disk, not from the app — it needs
  // no account at all.
  if (import.meta.env.DEV && ReelsPage && path === '/reels') {
    return (
      <Suspense fallback={null}>
        <ReelsPage onBack={() => navigate('/app', setPath)} />
      </Suspense>
    );
  }

  // Not signed in — show promo (except checkout/success which needs auth to restore)
  if (!user) {
    if (path === '/checkout/success') {
      // Auth is still restoring from Stripe redirect — wait
      return null;
    }
    if (sharedId) {
      dismissSplash();
      return (
        <Suspense fallback={null}>
          <SharedTechnique
            id={sharedId}
            onOpenApp={() => {
              // Come back to this technique once they're through sign-in.
              sessionStorage.setItem('gnosis-post-auth', path);
              navigate('/', setPath);
            }}
          />
        </Suspense>
      );
    }
    // "Start free" on the landing page — the real app, signed out. The library
    // index comes from a public endpoint and playback is capped (see
    // src/previewGate.ts); everything that belongs to an account raises the
    // wall instead of rendering empty.
    if (path === '/try') {
      dismissSplash();
      return (
        <div className="phone-frame-wrapper">
          <PhoneFrame
            ref={frameRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            style={frameX != null ? { position: 'relative', left: `${frameX}px` } : undefined}
          >
            <GuestProvider guest>
              <AppShell guest arts={arts.length ? arts : [Art.Bjj]} />
            </GuestProvider>
          </PhoneFrame>
        </div>
      );
    }
    if (path !== '/' && path !== '/promo') {
      navigate('/', setPath);
      return null;
    }
    return <Suspense fallback={null}><LandingPageV2 /></Suspense>;
  }

  // Signed in but subscription still loading
  if (subLoading || subStatus === null) return null;

  // Signed in from here on
  const isAdmin = user.uid === 'bZj8y9DApEac3eKOuKV08MKcaDx2';
  const hasAccess = subStatus === 'active' || isAdmin;

  // Signed-in users on / go straight to /app; /promo is always viewable
  if (path === '/') {
    navigate('/app', setPath);
    return null;
  }
  if (path === '/promo') {
    return <Suspense fallback={null}><LandingPageV2 /></Suspense>;
  }

  // /checkout/success — Stripe redirects here after payment.
  // Render over the app (like other modals) so the success modal sits on the app background.
  if (path === '/checkout/success') {
    return (
      <div className="phone-frame-wrapper">
        <PhoneFrame ref={frameRef} style={frameX != null ? { position: 'relative', left: `${frameX}px` } : undefined}>
          <AppShell arts={arts.length ? arts : ['bjj']} onChangeArts={setArts} />
        </PhoneFrame>
        <Suspense fallback={null}>
          <CheckoutSuccess sub={sub} onContinue={() => navigate('/app', setPath)} />
        </Suspense>
      </div>
    );
  }

  // /admin/chat — admin only
  if (path === '/admin/chat') {
    return <Suspense fallback={null}><AdminChat user={user} /></Suspense>;
  }

  // /admin/metrics — the traffic and money dashboard, admin only
  if (path === '/admin/metrics') {
    return <Suspense fallback={null}><AdminMetrics user={user} /></Suspense>;
  }

  // /checkout — show checkout modal (no app behind it)
  if (path === '/checkout') {
    return (
      <Suspense fallback={null}>
        <Checkout
          email={user.email || ''}
          uid={user.uid}
          sub={sub}
          onComplete={() => navigate('/app', setPath)}
          onCancel={() => navigate('/', setPath)}
        />
      </Suspense>
    );
  }

  // /app — the main app
  // Signed in + no access — show checkout WITHOUT mounting AppShell behind it.
  // (Previously AppShell rendered underneath, so deleting the paywall element
  // in devtools gave free access to the app.)
  if (!hasAccess) {
    if (sharedId) {
      dismissSplash();
      return (
        <Suspense fallback={null}>
          <SharedTechnique id={sharedId} onOpenApp={() => navigate('/checkout', setPath)} />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={null}>
        <Checkout
          email={user.email || ''}
          uid={user.uid}
          sub={sub}
          onComplete={() => navigate('/checkout/success', setPath)}
          onCancel={() => { logOut(); navigate('/', setPath); }}
          onSignOut={() => { logOut(); navigate('/', setPath); }}
        />
      </Suspense>
    );
  }

  // Signed in + has access but no arts chosen yet — full-screen "Show me…"
  // gate. Rendered inside the phone frame so desktop looks consistent.
  if (arts.length === 0) {
    return (
      <div className="phone-frame-wrapper">
        <PhoneFrame>
          <ArtPicker current={[]} onPick={setArts} />
        </PhoneFrame>
      </div>
    );
  }

  // Signed in + has access — show the app
  return (
    <div className="phone-frame-wrapper">
      <PhoneFrame
        ref={frameRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={frameX != null ? { position: 'relative', left: `${frameX}px` } : undefined}
      >
        <AppShell arts={arts} onChangeArts={setArts} initialTechniqueId={sharedId ?? undefined} />
      </PhoneFrame>
    </div>
  );
}

export default App;
